import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { Grid } from '@mui/material';
import useIntegration from 'api/useIntegration';

import AcCard from 'components/AcCard/AcCard';
import DataTable from 'components/DataTable/DataTable';
import FirstActionModal from 'components/FirstActionModal/FirstActionModal';
import CustomizedSwitch from 'components/SwitchButton/SwitchButton';

import useUsers from '../../../api/useUsers';
import { EFeatureFlag, EventCategories } from '../../../constants/enums';
import { EToasterStatus, showToast } from '../../../utils/showToast';
import { EPublisherEventValue, PublisherEventType } from '../Settings.types';

interface EventCategorySectionProps {
  category: string;
  events: PublisherEventType[];
  enabledEvents: string[];
  isTableDataEmpty: boolean;
}

const EventCategorySection: React.FC<EventCategorySectionProps> = ({
  category,
  events,
  enabledEvents,
  isTableDataEmpty
}) => {
  const { publisherId } = useParams();

  const { updateIntegration }: any = useIntegration(publisherId);
  const { fetchFeatureFlags } = useUsers({ publisherId });

  const areDeeplinksEnabled = useMemo(
    () =>
      fetchFeatureFlags.data?.featureFlags?.[
        EFeatureFlag.DASHBOARD_DEEPLINKS
      ],
    [fetchFeatureFlags.data]
  );

  const filteredEvents = useMemo(() => {
    if (
      category === EventCategories.WEB_STORE &&
      !areDeeplinksEnabled
    ) {
      return events.filter(
        (event) =>
          event.value !== EPublisherEventValue.APPROVAL_REQUEST_CLOSED &&
          event.value !== EPublisherEventValue.APPROVAL_REQUEST_DISPLAYED
      );
    }
    return events;
  }, [areDeeplinksEnabled, category, events]);

  const getToggleHandler = (isEnabled: boolean, eventKey: string) => {
    return () => {
      const updatedEvents = isEnabled
        ? enabledEvents.filter((e: string) => e !== eventKey)
        : [...enabledEvents, eventKey];

      updateIntegration.mutate(
        { enabledWebhookEvents: updatedEvents },
        {
          onSuccess: () => {
            showToast({
              message: 'Webhook event updated successfully',
              status: EToasterStatus.SUCCESS
            });
          },
          onError: () => {
            showToast({
              message: 'Something went wrong with updating the event',
              status: EToasterStatus.ERROR
            });
          }
        }
      );
    };
  };

  const columns = [
    {
      field: '_id',
      headerName: '',
      disableReorder: true,
      flex: 1
    },
    {
      field: 'name',
      headerName: 'Trigger',
      disableReorder: true,
      flex: 1,
      sortable: false
    },
    {
      field: 'key',
      headerName: 'Key',
      disableReorder: true,
      flex: 1
    },
    {
      field: 'value',
      headerName: 'Event Name',
      disableReorder: true,
      flex: 1,
      sortable: false
    },
    {
      field: 'subCategory',
      headerName: 'Category',
      disableReorder: true,
      flex: 1,
      sortable: false
    },
    {
      field: 'isActive',
      headerName: 'Is Active',
      disableReorder: true,
      flex: 1,
      sortable: false,
      renderCell: (params: any) => {
        const isEnabled = enabledEvents.includes(params.row.key);
        const handleToggle = getToggleHandler(isEnabled, params.row.key);
        return (
          <CustomizedSwitch
            status={isEnabled}
            texts={[]}
            functions={[handleToggle, handleToggle]}
          />
        );
      }
    }
  ];

  const rows = filteredEvents.map((event: PublisherEventType, i: number) => ({
    _id: i,
    name: event.displayName,
    value: event.value,
    key: event.value,
    subCategory: event?.subCategory
  }));

  return (
    <Grid container columnSpacing={4}>
      <Grid item mt={5} xs={15}>
        <AcCard stackContainer={false} title={category}>
          <DataTable
            columns={columns}
            rows={rows}
            defaultHiddenFields={['_id', 'key']}
            loading={false}
            onNoData={
              isTableDataEmpty ? (
                <FirstActionModal
                  headline="No events found"
                  text="Please check your spelling or try different keywords."
                />
              ) : (
                <FirstActionModal
                  headline="No exact matches"
                  text="Try changing or removing your filter or adjusting your search area"
                />
              )
            }
            className="eventsTable"
          />
        </AcCard>
      </Grid>
    </Grid>
  );
};

export default EventCategorySection;
